import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import rg4js from "raygun4js";
import { useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { useHistory } from "react-router-dom";
import { DocumentSubType, Role } from "@/briefpoint-client";
import Button from "@/components/Button";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import Loading from "@/components/Loading";
import SignUpModal from "@/components/Modals/SignUpModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { TEMPLATES_PROPOUNDING_FF } from "@/hooks/useConfig";
import { CaliforniaId } from "@/lib/constants";
import type {
  CaseResponse,
  JurisdictionResponse,
  UserResponse,
} from "@/lib/types";
import {
  isInTrialOrHasActiveSubscription,
  leadAttorneyName,
  toLocaleUSDateString,
} from "@/lib/utils";
import { getCaseSourceLogo } from "@/utils/ExternalPartner/utils";
import Pagination from "../Pagination/Pagination";
import styles from "./CaseTableMulti.module.scss";

interface Props {
  addCase: () => void;
  cases: CaseResponse[] | undefined;
  jurisdictions: JurisdictionResponse[] | undefined;
  users: UserResponse[] | null;
  recent?: boolean;
}

export default function CaseTableMulti({
  addCase,
  cases,
  jurisdictions,
  users,
  recent = false,
}: Props) {
  const { user, firm } = useAuth();
  const [, featureFlags] = useConfig();
  const useTemplatesOnlyPropounding = featureFlags()[TEMPLATES_PROPOUNDING_FF]
    ? featureFlags()[TEMPLATES_PROPOUNDING_FF](user, firm)
    : false;
  const history = useHistory();
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const pendingDraftType = useRef<DocumentSubType>(DocumentSubType.Unknown);
  const pendingCase = useRef<CaseResponse | null>(null);

  const hasProvidedInfo = useMemo(() => {
    return !!(
      user?.firstName &&
      user?.lastName &&
      user?.barNumber &&
      (!user?.role?.includes(Role.FirmAdmin) ||
        (firm?.name && firm?.address?.street && firm?.address.postalCode))
    );
  }, [
    user?.firstName,
    user?.lastName,
    user?.barNumber,
    user?.role,
    firm?.name,
    firm?.address?.street,
    firm?.address?.postalCode,
  ]);

  function sortByLastActivity(a: CaseResponse, b: CaseResponse) {
    return (b.lastActivity?.getTime() ?? 0) - (a.lastActivity?.getTime() ?? 0);
  }

  function venueDisplay(jurisdiction: string, id: string) {
    const j = jurisdictions?.find((x) => x.id === jurisdiction);
    return j?.venues?.find((x) => x.id === id)?.shortName ?? "";
  }

  function getJurisdictionForCase(_case: CaseResponse) {
    return jurisdictions?.find((x) => x.id === _case.jurisdiction);
  }

  function showSignupPage(_case: CaseResponse, value: DocumentSubType) {
    rg4js("trackEvent", {
      type: "pageView",
      path: "/signup-modal",
    });
    pendingDraftType.current = value;
    pendingCase.current = _case;
    setIsSignUpModalShow(true);
  }

  function handleSelectGenerate(
    _case: CaseResponse,
    value: DocumentSubType,
  ): void {
    pendingDraftType.current = DocumentSubType.Unknown;
    // show info page if user has not provided info yet, else generate word document
    if (hasProvidedInfo) {
      if (!user?.isInactive && firm && isInTrialOrHasActiveSubscription(firm)) {
        // Navigate to case page with generate parameter
        history.push({
          pathname: `/cases/${_case.id}`,
          search: `?generate=${value}`,
          state: {
            case: _case,
          },
        });
      } else {
        showSignupPage(_case, value);
      }
    } else {
      pendingDraftType.current = value;
      pendingCase.current = _case;
      setShowInfoModal(true);
    }
  }

  function navigateToCaseWithGenerate(
    _case: CaseResponse,
    value: DocumentSubType,
  ) {
    history.push({
      pathname: `/cases/${_case.id}`,
      search: `?generate=${value}`,
      state: {
        case: _case,
      },
    });
  }

  /* start table columns */
  const columns: ColumnDef<CaseResponse>[] = [
    {
      accessorKey: "name",
      accessorFn: (row) => row.shortTitle || row.title,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Title
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      cell: (data) => {
        return (
          <div className={`d-flex gap-2 align-items-center ${styles.cellData}`}>
            <div style={{ width: "18px" }}>
              {getCaseSourceLogo(data.row.original.integration?.partner)}
            </div>
            <div>{data.row.original.shortTitle || data.row.original.title}</div>
          </div>
        );
      },
      enableSorting: true,
      sortDescFirst: true,
      meta: {
        width: "25%",
      },
    },
    {
      accessorKey: "caseNumber",
      accessorFn: (row) => row.caseNumber,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Case Number
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      cell: (data) => (
        <div className={styles.cellData}>{data.row.original.caseNumber}</div>
      ),
      enableSorting: true,
      sortDescFirst: true,
      sortingFn: "text",
      meta: {
        width: "20%",
      },
    },
    {
      accessorKey: "venue",
      accessorFn: (row) =>
        venueDisplay(row.jurisdiction ?? "", row.venue ?? ""),
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Venue
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      cell: (data) => {
        return (
          <div className={styles.cellData}>
            {venueDisplay(
              data.row.original.jurisdiction ?? "",
              data.row.original.venue ?? "",
            )}
          </div>
        );
      },
      enableSorting: true,
      sortDescFirst: true,
      sortingFn: "text",
      meta: {
        width: "15%",
      },
    },
    {
      accessorKey: "leadAttorney",
      accessorFn: (row) => leadAttorneyName(row, users ?? []),
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Lead Attorney
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      cell: (data) => (
        <div className={styles.cellData}>
          {leadAttorneyName(data.row.original, users ?? [])}
        </div>
      ),
      enableSorting: true,
      sortDescFirst: true,
      sortingFn: "text",
      meta: {
        width: "15%",
      },
    },
    {
      accessorKey: "lastActivity",
      accessorFn: (row) =>
        row.lastActivity ? new Date(row.lastActivity) : null,
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Last Activity
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      cell: (data) => (
        <div className={styles.cellData}>
          {data.row.original.lastActivity
            ? toLocaleUSDateString(data.row.original.lastActivity)
            : ""}
        </div>
      ),
      meta: {
        width: "10%",
      },
      enableSorting: true,
      sortDescFirst: true,
      sortingFn: (rowA, rowB) => {
        return rowA.original.lastActivity && rowB.original.lastActivity
          ? rowA.original.lastActivity.getTime() -
              rowB.original.lastActivity.getTime()
          : 0;
      },
    },
    {
      accessorKey: "actions",
      header: "",
      cell: (data) => (
        <div className="d-flex justify-content-end gap-2">
          {useTemplatesOnlyPropounding && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  id={`draft-dropdown-${data.row.original.id}`}
                  className="btn-26 draft-dropdown-button"
                >
                  Draft Request
                  <ChevronDown className="ms-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom">
                <DropdownMenuItem
                  onClick={() =>
                    handleSelectGenerate(
                      data.row.original,
                      DocumentSubType.RequestsForAdmission,
                    )
                  }
                >
                  Request for Admission
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    handleSelectGenerate(
                      data.row.original,
                      DocumentSubType.RequestsForProduction,
                    )
                  }
                >
                  Request for Production
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    handleSelectGenerate(
                      data.row.original,
                      DocumentSubType.SpecialInterrogatorries,
                    )
                  }
                >
                  {getJurisdictionForCase(data.row.original)?.id ===
                  CaliforniaId
                    ? "Special Interrogatories"
                    : "Interrogatories"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            data-testid="view-case-button"
            variant="default"
            className="btn-26"
            to={{
              pathname: `/cases/${data.row.original.id}`,
              state: { case: data.row.original },
            }}
          >
            View Case
          </Button>
        </div>
      ),
      meta: {
        width: "15%",
      },
    },
  ];
  /* end table columns */

  /* start table */
  const table = useReactTable({
    data:
      cases
        ?.sort(recent ? sortByLastActivity : undefined)
        .slice(0, recent ? 5 : cases?.length) ?? [],
    columns,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    autoResetAll: false,
    initialState: {
      sorting: recent
        ? [{ id: "lastActivity", desc: true }]
        : [{ id: "name", desc: false }],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });
  /* end table */

  return (
    <Loading
      isLoading={
        cases === undefined &&
        jurisdictions === undefined &&
        users === undefined
      }
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className={styles.tableHeaderRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      width: (cell.column.columnDef.meta as { width: string })
                        ?.width, // taken from column meta definition
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <div className="d-flex gap-1 align-items-center">
                  There are no Cases yet:
                  <Button variant="link" onClick={addCase} className="p-0">
                    Add one now
                  </Button>
                  or just start by uploading a document.
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="d-flex justify-content-end py-2">
        {table.getPageCount() > 1 && (
          <Pagination
            navigate={(page) => table.setPageIndex(page - 1)}
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            totalItemCount={cases?.length ?? 0}
            itemsPerPage={table.getState().pagination.pageSize}
            showPageCount
          />
        )}
      </div>

      <SignUpModal
        isShow={isSignUpModalShow}
        onClose={(success) => {
          setIsSignUpModalShow(false);
          if (success && pendingDraftType.current && pendingCase.current) {
            navigateToCaseWithGenerate(
              pendingCase.current,
              pendingDraftType.current,
            );
          }
          pendingDraftType.current = DocumentSubType.Unknown;
          pendingCase.current = null;
        }}
      />
      {!hasProvidedInfo && (
        <Modal
          show={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          size="xl"
          title={"Information for output document"}
          showCancelButton={false}
          showConfirmButton={false}
          preventBackgroundClick
        >
          <GeneralInformationScreen
            documentId={"anything"}
            onSave={async () => {
              setShowInfoModal(false);
              if (pendingDraftType.current && pendingCase.current) {
                navigateToCaseWithGenerate(
                  pendingCase.current,
                  pendingDraftType.current,
                );
              }
              pendingDraftType.current = DocumentSubType.Unknown;
              pendingCase.current = null;
            }}
          />
        </Modal>
      )}
    </Loading>
  );
}
